/**
 * Hybrid Navigation Router Guard for Nuxt 4
 *
 * Intercepts navigation to non-Nuxt4 routes and forces full page reload to Nuxt 2.
 * This runs before route components load, preventing flash of Nuxt 4 content.
 */
const supportedLanguages = ['fr', 'de']

const normalizePath = (path: string): string => {
  const normalized = path.startsWith('/') ? path : `/${path}`
  const locale = supportedLanguages.find(language => normalized === `/${language}` || normalized.startsWith(`/${language}/`))
  return locale ? normalized.slice(locale.length + 1) || '/' : normalized
}

export const matchesHybridRoute = (path: string, routes: string[]) => {
  const normalizedPath = normalizePath(path)

  return routes.some((route) => {
    if (route === '*') {
      return true
    }

    const normalizedRoute = normalizePath(route)
    if (normalizedRoute === '/') {
      return normalizedPath === '/'
    }
    return normalizedPath === normalizedRoute || normalizedPath.startsWith(`${normalizedRoute}/`)
  })
}

export const parseHybridRoutes = (...sources: unknown[]): string[] => sources
  .flatMap(source => Array.isArray(source) ? source : [source])
  .filter((route): route is string => typeof route === 'string')
  .flatMap(route => route.split(','))
  .map(route => route.trim())
  .filter(Boolean)

interface RouteLike {
  path: string
  fullPath: string
}

interface LocationLike {
  href: string
}

// Returning `false` cancels the navigation — exactly what the deprecated
// `next(false)` did here. The browser is being handed to the legacy stack via
// `location.href`, so the in-app route change must NOT also proceed.
const redirectToLegacy = (to: RouteLike, location: LocationLike): false => {
  location.href = to.fullPath
  return false
}

export const createHybridNavigationGuard = (
  routes: string[],
  location: LocationLike,
) => {
  let isInitialNavigation = true

  // Returning undefined allows the navigation; returning false cancels it.
  return (to: RouteLike, _from?: unknown): false | undefined => {
    if (isInitialNavigation) {
      isInitialNavigation = false
      return
    }

    if (matchesHybridRoute(to.path, routes)) {
      return
    }

    // Non-Nuxt4 route - force reload through the proxy to Nuxt 2, whatever the
    // user's role. Do not gate matched routes on role: the ingress is
    // role-agnostic, so sending a demonstrator on a matched route back to
    // Nuxt 2 would loop (the edge returns them to Nuxt 4). AB#63637
    return redirectToLegacy(to, location)
  }
}

export default defineNuxtPlugin(() => {
  const router = useRouter()
  const config = useRuntimeConfig()
  const persistentStore = usePersistentStore()
  const nuxt4Routes = parseHybridRoutes(
    config.public.HYBRID_ROUTES,
    persistentStore.getPersistentValue('hybridRoute'),
  )

  router.beforeEach(createHybridNavigationGuard(nuxt4Routes, window.location))
})
